import { StoreId } from "../../decorators/StoreIdDecorator";
import { isMergeOption, MergeOption } from "../../types/MergeOptions";
import { isMergeVersion, MergeVersion } from "../../types/MergeVersion";
import {
  isNormalizationType,
  NormalizationType,
} from "../../types/NormalizationType";
import API from "../API";
import DocTypeConfig, { DocTypeConfigData } from "./DocTypeConfig";
import { HeliosBatchClassConfig } from "./helios/BatchClassConfig";

// Export Batch Class config models
export {
  BatchClassConfig,
  BatchClassConfigData,
  BatchClassConfigSettings,
  createSkeletonBatchClass,
};
// Export Batch Class Config functions
export { getNextPosition, getNextHeliosPosition };

/* Configurable BatchClassConfig settings within Accelerator */
type BatchClassConfigSettings = {
  populateUnextractedTablesWithReuse: boolean;
  sendAllBatches: boolean;
  sendOnEmpty: boolean;
  autoClassify: boolean;
  acceleratorOcrThresholds: boolean;
  autoClassifyType: string | null;
  mergeType: MergeOption;
  mergeVersion: MergeVersion;
  normalizationType: NormalizationType;
  defaultMatcherThreshold: number;
  report: boolean;
  forceReview: boolean;
  /**
   * 0 - 100 value for default OCR confidence.
   */
  ocrConfidenceThreshold: number;
};

type BatchClassConfigData = {
  uuid: string;
  batchClassId: string;
  batchClassName: string;
  docTypes: DocTypeConfigData[];

  helios?: HeliosBatchClassConfig;

  /**
   * -1 indicates this is a new BCC that is not saved.
   */
  version: number;
  /* Unix Epoch in MS of when created. */
  created?: number;
  /* Unix Epoch in MS of when last modified. */
  lastModified?: number;
  /* String value representing who last modified BCC. */
  modifiedBy?: string;
  /**
   * Defined if current BCC is considered "stale". This indicates that there is a newer version of the MoBCCdel the
   * user might prefer.
   * If staleness is not a concern, this.version can be set to this.knownHigherVersion.
   * Set by FE.
   */
  knownHigherVersion?: number;
} & BatchClassConfigSettings;

function createSkeletonBatchClass(id: string, name: string): BatchClassConfig {
  return new BatchClassConfig({
    uuid: "",
    batchClassId: id,
    batchClassName: name,
    autoClassify: false,
    autoClassifyType: "Unknown",
    defaultMatcherThreshold: BatchClassConfig.DEFAULT_MATCHER_THRESHOLD,
    ocrConfidenceThreshold: BatchClassConfig.DEFAULT_OCR_THRESHOLD,
    docTypes: [DocTypeConfig.getUnknown()],
    mergeType: "HYBRID",
    mergeVersion: "OLD",
    normalizationType: BatchClassConfig.DEFAULT_NORMALIZATION,
    populateUnextractedTablesWithReuse: false,
    report: false,
    sendAllBatches: false,
    sendOnEmpty: false,
    acceleratorOcrThresholds: true,
    version: -1,
    forceReview: false,
  });
}

// FIXME: does this need a changed property ?
class BatchClassConfig {
  static DEFAULT_MATCHER_THRESHOLD = 80;
  static DEFAULT_NORMALIZATION: NormalizationType = "LINE_NORMALIZED";
  static DEFAULT_OCR_THRESHOLD = 90;
  uuid: string;
  @StoreId
  batchClassId: string;
  _batchClassName: string = "";
  set batchClassName(name: string) {
    let tmpName = name.trim();
    if (tmpName.length > 255) {
      tmpName = tmpName.substring(0, 254);
    }
    this._batchClassName = tmpName;
  }
  get batchClassName() {
    return this._batchClassName;
  }
  populateUnextractedTablesWithReuse: boolean;
  sendAllBatches: boolean;
  sendOnEmpty: boolean;
  autoClassify: boolean;
  acceleratorOcrThresholds: boolean;
  autoClassifyType: string | null;
  defaultMatcherThreshold: number;
  /**
   * 0 - 100 value for default OCR confidence.
   */
  ocrConfidenceThreshold: number;

  docTypes: DocTypeConfig[];

  forceReview: boolean;
  report: boolean;
  version: number;
  created?: number;
  lastModified?: number;
  modifiedBy?: string;

  helios?: HeliosBatchClassConfig;

  _mergeType: MergeOption = "HYBRID";
  _mergeVersion: MergeVersion = "OLD";
  _normalizationType: NormalizationType = "NATIVE";

  roles: Set<number> = new Set();

  set mergeType(value: MergeOption) {
    if (isMergeOption(value)) this._mergeType = value;
  }
  set mergeVersion(value: MergeVersion) {
    if (isMergeVersion(value)) this._mergeVersion = value;
  }
  set normalizationType(value: NormalizationType) {
    if (isNormalizationType(value)) this._normalizationType = value;
  }

  get mergeType(): MergeOption {
    return this._mergeType;
  }
  get mergeVersion(): MergeVersion {
    return this._mergeVersion;
  }
  get normalizationType(): NormalizationType {
    return this._normalizationType;
  }

  constructor(data: BatchClassConfigData) {
    this.uuid = data.uuid;

    this.created = data.created;
    this.lastModified = data.lastModified;
    this.modifiedBy = data.modifiedBy;
    this.report = data.report;
    this.forceReview = data.forceReview != undefined ? data.forceReview : false;

    this.helios = data.helios;
    this.version = data.version;
    this.batchClassId = data.batchClassId;
    this.batchClassName = data.batchClassName;
    this.populateUnextractedTablesWithReuse =
      data.populateUnextractedTablesWithReuse;
    this.sendAllBatches = data.sendAllBatches;
    this.sendOnEmpty = data.sendOnEmpty;
    this.acceleratorOcrThresholds = data.acceleratorOcrThresholds;
    this.autoClassify = data.autoClassify;
    this.autoClassifyType = data.autoClassifyType;
    this.defaultMatcherThreshold = data.defaultMatcherThreshold;
    this.ocrConfidenceThreshold =
      data.ocrConfidenceThreshold ?? BatchClassConfig.DEFAULT_OCR_THRESHOLD;

    this.docTypes = data.docTypes.map(
      (dt) => new DocTypeConfig(dt, this.defaultMatcherThreshold),
    );

    this.mergeType = data.mergeType;
    this.mergeVersion = data.mergeVersion;
    this.normalizationType = data.normalizationType;

    this.roles = new Set();

    this.docTypes
      .filter((a) => a.helios != undefined)
      .sort((a, b) => a.helios!.position - b.helios!.position);
  }

  updateSetting<
    K extends keyof BatchClassConfigSettings,
    V extends BatchClassConfigSettings[K],
  >(setting: K, value: V): BatchClassConfig {
    console.debug(`Updating BCC ${setting} with ${value}`);
    return this.updateSettings({
      ...this,
      [setting]: value,
    });
  }

  updateSettings(s: BatchClassConfigSettings): BatchClassConfig {
    this.autoClassify = s.autoClassify;
    this.autoClassifyType = s.autoClassifyType;
    this.defaultMatcherThreshold = s.defaultMatcherThreshold;
    this.forceReview = s.forceReview;
    this.mergeType = s.mergeType;
    this.mergeVersion = s.mergeVersion;
    this.normalizationType = s.normalizationType;
    this.populateUnextractedTablesWithReuse =
      s.populateUnextractedTablesWithReuse;
    this.report = s.report;
    this.sendAllBatches = s.sendAllBatches;
    this.sendOnEmpty = s.sendOnEmpty;
    this.ocrConfidenceThreshold = s.ocrConfidenceThreshold;
    this.acceleratorOcrThresholds = s.acceleratorOcrThresholds;
    return this;
  }

  updateSettingHellios<
    K extends keyof HeliosBatchClassConfig,
    V extends HeliosBatchClassConfig[K],
  >(setting: K, value: V): BatchClassConfig {
    console.debug(`Updating BCC ${setting} with ${value}`);
    return this.updateSettingsHelios({
      ...this.helios!,
      [setting]: value,
    });
  }

  updateSettingsHelios(s: HeliosBatchClassConfig): BatchClassConfig {
    if (!this.helios)
      throw new Error("Cannot update helios settings if helios is invalid");
    this.helios.batchClassDescription = s.batchClassDescription;
    this.helios.batchClassFields = [...s.batchClassFields];
    this.helios.priority = s.priority;
    return this;
  }

  getDocumentType(name: string | undefined): DocTypeConfig | undefined {
    if (name == undefined) return undefined;

    const docTypes: DocTypeConfig[] = this.docTypes.filter(
      (x) => x.typeName === name,
    );
    if (docTypes.length > 0) {
      return docTypes[0];
    }
    return undefined;
  }

  //TODO: move
  static async syncBatchClassConfigs(): Promise<BatchClassConfig[]> {
    const bccs: BatchClassConfig[] =
      await BatchClassConfig.syncBatchClassConfigsWithoutRoles();

    await Promise.all(bccs.map((bcc) => bcc.syncRoles()));

    return bccs;
  }
  //TODO: move
  static async syncBatchClassConfigsWithoutRoles(): Promise<
    BatchClassConfig[]
  > {
    let responseValue: any = await API.get("/v0/config")
      .then((value: Response) => value.json())
      .catch((error: string) => {
        console.log(error);
      });

    const batchClassConfigs: BatchClassConfig[] = [];

    if (!Array.isArray(responseValue)) responseValue = [];

    responseValue
      .map((x: any): BatchClassConfig | null => {
        try {
          return new BatchClassConfig(x);
        } catch (e) {
          console.error(e);
          return null;
        }
      })
      .filter((x: BatchClassConfig | null) => x != null)
      .forEach((x: BatchClassConfig) => batchClassConfigs.push(x));

    // await Promise.all(batchClassConfigs.map(bcc => bcc.syncRoles()));

    return batchClassConfigs;
  }

  /**
   * Loads the Role and Permission data for this batch class
   */
  // TODO: move
  async syncRoles() {
    if (this.roles.size > 0) return this.roles; // FIXME: the most temporary solutions are the most resilient to time
    this.roles = await API.get(`/v0/role`, {
      batchClassId: this.batchClassId,
    })
      .then((value: Response) => value.json())
      .then((roles: number[]) =>
        roles.reduce(
          (prev: Set<number>, role: number, idx: number) => prev.add(role),
          new Set<number>(),
        ),
      )
      .catch(() => {
        return new Set<number>();
      });

    return this.roles;
  }

  toData(): BatchClassConfigData {
    return {
      uuid: this.uuid,
      created: this.created,
      lastModified: this.lastModified,
      modifiedBy: this.modifiedBy,
      report: this.report,
      acceleratorOcrThresholds: this.acceleratorOcrThresholds,
      // FIXME: helios should be cloned
      helios: this.helios ? { ...this.helios } : undefined,
      version: this.version,
      autoClassify: this.autoClassify,
      autoClassifyType: this.autoClassifyType,
      batchClassId: this.batchClassId,
      batchClassName: this.batchClassName,
      docTypes: this.docTypes.map((docType) => docType),
      mergeType: this.mergeType,
      mergeVersion: this.mergeVersion,
      normalizationType: this.normalizationType,
      sendAllBatches: this.sendAllBatches,
      sendOnEmpty: this.sendOnEmpty,
      populateUnextractedTablesWithReuse:
        this.populateUnextractedTablesWithReuse,
      defaultMatcherThreshold: this.defaultMatcherThreshold,
      forceReview: this.forceReview,
      ocrConfidenceThreshold: this.ocrConfidenceThreshold,
    };
  }

  public static createTest(
    rest: Partial<BatchClassConfigData> = {},
  ): BatchClassConfig {
    const defaultBcc: BatchClassConfigData = {
      uuid: "",
      created: undefined,
      lastModified: undefined,
      modifiedBy: "",
      version: -1,
      batchClassId: "",
      batchClassName: "",
      report: true,
      forceReview: false,
      populateUnextractedTablesWithReuse: false,
      acceleratorOcrThresholds: false,
      sendAllBatches: false,
      sendOnEmpty: false,
      autoClassify: false,
      autoClassifyType: null,
      docTypes: [],
      mergeType: "PATTERN",
      mergeVersion: "NEW",
      normalizationType: BatchClassConfig.DEFAULT_NORMALIZATION,
      defaultMatcherThreshold: this.DEFAULT_MATCHER_THRESHOLD,
      ocrConfidenceThreshold: this.DEFAULT_OCR_THRESHOLD,
    };
    return new BatchClassConfig({
      ...defaultBcc,
      ...rest,
    });
  }
}

type HasPosition = { position: number };
type HasHeliosPosition = { helios?: { position: number } };

/**
 * Get next position for Config object arrays
 * @param arr
 * @returns next position
 */
function getNextPosition(arr: HasPosition[]): number {
  let max = 0;
  for (const entry of arr) {
    if (entry.position > max) {
      max = entry.position;
    }
  }
  return max + 1;
}
/**
 * Get next position for Helios Config object arrays
 * @param arr
 * @returns next position
 */
function getNextHeliosPosition(arr: HasHeliosPosition[]): number {
  let max = 0;
  for (const entry of arr) {
    if (entry.helios && entry.helios.position > max) {
      max = entry.helios.position;
    }
  }
  return max + 1;
}
